import { useEffect, useState } from 'react'
import { useIsFetching, useQueryClient, type QueryFilters } from '@tanstack/react-query'
import { Button, Empty, Skeleton } from '../ui'

export function Stub({ next }: { next: string }) {
  return (
    <Empty title="Scaffold">
      This screen is wired to the database but not built yet. Next: {next}
    </Empty>
  )
}

/** How long a load may take before the screen says so (UI audit a1-34). A
 *  failing read is retried with backoff before it is an error at all
 *  (`store.ts`), which is 7–12 s of skeleton with nothing said; 4 s is past
 *  any ordinary cold load and well inside that. */
export const SLOW_LOAD_MS = 4000

/**
 * The waiting state for a screen: a skeleton in the shape of what replaces it
 * (`variant`, `Skeleton`'s own — `row` for a list), and after `SLOW_LOAD_MS`
 * one line saying it is slow, so a skeleton that is never going to resolve
 * is not the only thing on screen for ten seconds.
 */
export function Loading({ variant = 'lines', count }: { variant?: 'row' | 'lines'; count?: number }) {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_LOAD_MS)
    return () => clearTimeout(t)
  }, [])
  return (
    <>
      {slow && <p className="loadslow" role="status">Taking longer than usual…</p>}
      <Skeleton variant={variant} count={count} />
    </>
  )
}

/** The queries a Retry is for: the ones on screen that ended in an error. */
const FAILED: QueryFilters = { type: 'active', predicate: q => q.state.status === 'error' }

/** A read failure, on any screen. The migrations hint used to be the whole
 *  of it — a sentence for whoever is at a terminal, and nothing at all for
 *  Doug beyond the raw Postgres error, with no way back short of leaving
 *  the tab and returning (UI-audit F23). The hint is real and stays, but
 *  only where it can be read as a hint: `import.meta.env.DEV` is false in
 *  the deployed app.
 *
 *  a2-30: the error itself shows in the deployed app too, in small print
 *  under the sentence — "Something went wrong" alone gave Doug nothing to
 *  report, and nothing to tell a 500 from a dropped connection.
 *
 *  Retry refetches, never reloads, wherever it can (a1-34, a2-30). A reload
 *  threw away the signed-in shell, every other tab's state and the warm
 *  cache, and dropped any open sheet, to retry one read. A screen that
 *  knows its own failed queries passes `onRetry` (Lists, Year); for every
 *  other caller this finds them (`FAILED`: active and in error — the ones
 *  the screen is showing the error of) and refetches those. The button is
 *  busy while they run; success re-renders the screen out of this state, a
 *  second failure leaves it here. Only an error with no failed query behind
 *  it — not a read at all — still reloads. */
export function Failed({ error, onRetry }: { error: string; onRetry?: () => void }) {
  const qc = useQueryClient()
  const busy = useIsFetching(FAILED) > 0
  const retry = () => {
    if (onRetry) { onRetry(); return }
    if (qc.getQueryCache().findAll(FAILED).length === 0) { window.location.reload(); return }
    void qc.refetchQueries(FAILED)
  }
  return (
    <Empty title="Couldn't load this.">
      {import.meta.env.DEV ? (
        <>
          {error}
          <br /><br />
          If this says the relation does not exist, the migrations have not been applied yet.
        </>
      ) : (
        <>
          <p>Something went wrong reading this screen.</p>
          <p className="failed__detail">{error}</p>
        </>
      )}
      <Button variant="secondary" size="sm" busy={busy} onClick={retry}>Retry</Button>
    </Empty>
  )
}
